/**
 * Turning a {@link PayrollPeriod} into the exact INSTANTS to query sessions with.
 *
 * `payroll-period.ts` deliberately deals only in `YYYY-MM-DD` strings and UTC
 * arithmetic — a period is a pair of calendar dates, and that must resolve the
 * same everywhere. But `GET /time/sessions` filters `clock_in_at`, a
 * TIMESTAMPTZ, so asking it for a period means naming two moments in time. That
 * conversion is what lives here, and it is separate precisely so the period
 * arithmetic stays timezone-free.
 *
 * TWO TRAPS THIS EXISTS TO AVOID, both of which a bare date string walks into:
 *
 *  1. `to` IS EXCLUSIVE. The API applies `clock_in_at < :to`, so passing the
 *     period's last day (a Tuesday) drops every one of that Tuesday's shifts —
 *     a whole day of payroll, missing, with nothing to indicate it. The window
 *     therefore ends at the START of the following day.
 *
 *  2. A bare `YYYY-MM-DD` is midnight UTC, not midnight in the payroll
 *     timezone. For US-Central that is 6–7pm the PREVIOUS day, so a period
 *     boundary drawn that way pulls in the tail of the prior evening and loses
 *     the same slice at the far end. Both edges are wrong, in opposite
 *     directions, which is the hardest kind of wrong to notice.
 *
 * Everything here is pure: no clock, no locale, no `Date` parsing of anything
 * but ISO. `Intl` supplies the zone offset, so there is no timezone dependency.
 */
/**
 * The timezone payroll days are measured in.
 *
 * The tenant's `payrollTimezone` (NetSuite connection config) is the AUTHORITY;
 * this is the value it holds for J&J and the default used where the config is
 * not to hand — the timecard grid is Manager-visible while the connection
 * endpoint is not, so the client cannot always read it. If a tenant ever runs
 * payroll in another zone, pass its zone in explicitly rather than editing this.
 */
export const PAYROLL_TIME_ZONE = 'America/Chicago';
const MS_PER_DAY = 86_400_000;
/**
 * How far `timeZone` is from UTC at a given instant, in ms (positive = ahead).
 * Derived by formatting the instant in the zone and reading the wall clock back,
 * which is the only way to get a historical/DST-correct offset without a tz
 * database of our own.
 */
function zoneOffsetMs(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(new Date(instant));
    const read = (type) => {
        const found = parts.find((p) => p.type === type);
        return found === undefined ? 0 : Number(found.value);
    };
    // `hour` comes back as 24 for midnight under hour12:false in some engines.
    const wallClock = Date.UTC(read('year'), read('month') - 1, read('day'), read('hour') % 24, read('minute'), read('second'));
    return wallClock - instant;
}
/**
 * The instant at which `date` (an ISO `YYYY-MM-DD`) begins in `timeZone`.
 *
 * Solved in two passes: guess using the offset at UTC midnight, then re-read the
 * offset AT that guess and correct. One correction is enough for every real
 * zone — the second pass only matters across a DST transition, where the offset
 * at UTC midnight and at local midnight differ by the shift amount.
 */
export function zonedDayStart(date, timeZone = PAYROLL_TIME_ZONE) {
    const utcMidnight = Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)));
    const firstGuess = utcMidnight - zoneOffsetMs(utcMidnight, timeZone);
    const corrected = utcMidnight - zoneOffsetMs(firstGuess, timeZone);
    return new Date(corrected).toISOString();
}
/**
 * The instant window covering a whole payroll period in `timeZone`: from the
 * start of its first day to the start of the day AFTER its last, so the final
 * day is included under the API's exclusive upper bound.
 */
export function payrollPeriodWindow(period, timeZone = PAYROLL_TIME_ZONE) {
    const dayAfterEnd = new Date(Date.UTC(Number(period.endDate.slice(0, 4)), Number(period.endDate.slice(5, 7)) - 1, Number(period.endDate.slice(8, 10))) + MS_PER_DAY)
        .toISOString()
        .slice(0, 10);
    return {
        from: zonedDayStart(period.startDate, timeZone),
        to: zonedDayStart(dayAfterEnd, timeZone),
    };
}
/**
 * Today's calendar date in `timeZone`, as ISO `YYYY-MM-DD`.
 *
 * Which payroll period is "current" must be decided in the PAYROLL timezone, not
 * the viewer's: a supervisor opening the page at 11pm Tuesday from a zone one day
 * ahead would otherwise be shown next week's empty period.
 */
/**
 * How far into the local day it is, in minutes (0..1439), in `timeZone`.
 *
 * The companion to {@link todayInZone}, and the other half of what the scheduled-report
 * runner needs to decide whether a report is due: "what local day is it" is not enough
 * to answer "is it past 07:00 yet".
 *
 * 🔥 DERIVED FROM THE ZONE, NEVER FROM THE SERVER CLOCK. The containers run in UTC, so
 * `now.getHours()` would have every schedule fire five or six hours early — and would
 * SHIFT TWICE A YEAR as the offset changes, which is the kind of fault that gets
 * diagnosed as "the report is randomly late" for a season. `hourCycle: 'h23'` is
 * explicit because the default can yield "24" at midnight for some locales.
 */
export function minutesIntoDayInZone(now, timeZone = PAYROLL_TIME_ZONE) {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(now);
    const hour = Number.parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
    const minute = Number.parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10);
    if (Number.isNaN(hour) || Number.isNaN(minute)) {
        return 0;
    }
    // Modulo guards the h24 edge: some environments render midnight as hour 24.
    return (hour % 24) * 60 + minute;
}
export function todayInZone(now, timeZone = PAYROLL_TIME_ZONE) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
    // `en-CA` yields YYYY-MM-DD deterministically.
    return parts;
}
//# sourceMappingURL=payroll-window.js.map